import * as React from "react";
import styled from "@emotion/styled";
import { Button, AppError, Label } from "atoms";
import { formatSize } from "../utils/formatSize";

// 10mb = mb * kb * b
const DEFAULT_MAX_FILE_SIZE = 10 * 1000 * 1000;

const imageTypes = {
  values:
    ".jpg,.jpeg,image/jpeg,.png,image/png,.webp,image/webp,.gif,image/gif" as const,
  labels: ["JPG", "PNG", "GIF"] as const,
};
const videoTypes = {
  values: ".mp4,video/mp4" as const,
  labels: ["MP4"] as const,
};
const acceptTypes = {
  image: imageTypes,
  video: videoTypes,
  both: {
    values: `${imageTypes.values},${videoTypes.values}` as const,
    labels: [...imageTypes.labels, ...videoTypes.labels] as const,
  },
} as const;

const stopDragEvent: React.DragEventHandler = (event) => {
  event.stopPropagation();
  event.preventDefault();
};

type FileInputProps = {
  label?: React.ReactNode;
  /**
   * Optional limit for file size in bits, defaults to max allowed size
   */
  maxFileSize?: number;
  /**
   * Optional handler that renders a cancel button and runs on click
   */
  onCancel?: VoidFunction;
  /**
   * Handler that runs with selected File
   */
  onChange: (file: File) => void;
  /**
   * Optional attribute to accept certain file types
   */
  accept?: keyof typeof acceptTypes;
};

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      label,
      maxFileSize = DEFAULT_MAX_FILE_SIZE,
      onCancel,
      onChange,
      accept = "image",
    },
    ref
  ) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const [isImageTooLarge, setIsImageTooLarge] = React.useState(false);

    /**
     * Initialize cropper with new file from select or drop event's file
     */
    const handleFile = (file: File) => {
      if (file.size <= maxFileSize) {
        onChange(file);
      } else {
        setIsImageTooLarge(true);
      }
    };

    /**
     * Handle onChange event when selecting file through dialog menu
     */
    const handleSelectFile: React.ChangeEventHandler<HTMLInputElement> = (
      event
    ) => {
      if (event.target.files && event.target.files.length > 0) {
        handleFile(event.target.files[0]);
      }
    };

    /**
     * Update state to style on start of dragging event
     */
    const handleDragEnter: React.DragEventHandler = (event) => {
      stopDragEvent(event);
      setIsDragging(true);
    };

    /**
     * Update state to style on end of dragging event
     */
    const handleDragLeave: React.DragEventHandler = () => {
      setIsDragging(false);
    };

    /**
     * Handle onDrop event when selecting file through drag and drop
     */
    const handleDrop: React.DragEventHandler = (event) => {
      stopDragEvent(event);
      const files = event.dataTransfer?.files;
      if (files) handleFile(files[0]);
    };

    return (
      <Dropbox
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={stopDragEvent}
        onDrop={handleDrop}
      >
        {label && <FakeLabel>{label}</FakeLabel>}

        <div>
          <SButton variant="secondary" as="label">
            Choose File...
            <HiddenInput
              type="file"
              accept={acceptTypes[accept].values}
              onChange={handleSelectFile}
              ref={ref}
            />
          </SButton>
        </div>

        <div className="requirements">
          <small>
            Supports {acceptTypes[accept].labels.join(", ")} files. Animated
            GIFs not currently supported. {formatSize(maxFileSize)}
            &nbsp;max&nbsp;file&nbsp;size.
          </small>
        </div>

        {isImageTooLarge && (
          <AppError role="alert">
            The selected image exceeds the max file size of{" "}
            {formatSize(maxFileSize)}.
          </AppError>
        )}

        {onCancel && (
          <SButton onClick={onCancel} variant="blank" size="xxs">
            Cancel
          </SButton>
        )}
      </Dropbox>
    );
  }
);

type DropboxProps = {
  isDragging: boolean;
};

const Dropbox = styled.div<DropboxProps>(({ theme, isDragging }) => ({
  alignItems: "flex-start",
  // border: `2px dashed ${isDragging ? theme.colors.fg : theme.colors.fg20}`,
  border: "none",
  // borderRadius: theme.borderRadius.md,
  borderRadius: 0,
  padding: 0,
  "@media (prefers-reduced-motion: no-preference)": {
    transition: "border ease 0.3s",
  },
  width: "max-content",
  maxWidth: "100%",
  display: "flex",
  gap: ".2rem",
  flexDirection: "column",
  justifyContent: "space-around",
  "& > div": {
    width: "max-content",
    maxWidth: "100%",
  },
  "& p": {
    marginBottom: "0.5rem",
  },
  "& p:last-child": {
    marginBottom: "0",
  },
}));

const FakeLabel = styled(Label)({}).withComponent("div");

const HiddenInput = styled.input({
  clip: "rect(1px, 1px, 1px, 1px)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  width: 1,
});

const SButton = styled(Button)({
  "&&": { minHeight: 0 },
});
SButton.defaultProps = {
  size: "xs",
};
export { FileInput, HiddenInput };
